import React, { useState } from 'react';
import { AdminProtection } from '@/components/admin/AdminProtection';
import { SequenceEditor } from '@/components/admin/SequenceEditor';
import { AutoUpdateModal } from '@/components/admin/AutoUpdateModal';
import ClaimsManagement from '@/components/admin/ClaimsManagement';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminSequences } from '@/hooks/useAdminSequences';
import { Settings, Database, Upload, RefreshCw, BarChart3, UserCheck, FileCheck } from 'lucide-react';

type AdminTab = 'editor' | 'claims';

const AdminPage: React.FC = () => {
  const { sequences, loading } = useAdminSequences();
  const [showAutoUpdate, setShowAutoUpdate] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('editor');

  // Calculate statistics
  const stats = {
    totalSequences: sequences.length,
    withSpotifyData: sequences.filter(s => s.spotify_track_id).length,
    withArtist: sequences.filter(s => s.artist_id).length,
    withGenre: sequences.filter(s => s.genre_id).length,
    missingMetadata: sequences.filter(s => 
      !s.spotify_track_id || !s.artist_id || !s.genre_id || !s.spotify_popularity
    ).length
  };

  const TabButton: React.FC<{ 
    tab: AdminTab; 
    icon: React.ReactNode; 
    label: string; 
    description: string;
  }> = ({ tab, icon, label, description }) => (
    <Button
      variant={activeTab === tab ? 'default' : 'outline'}
      onClick={() => setActiveTab(tab)}
      className="flex flex-col items-center gap-2 h-auto p-6 text-left"
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-medium">{label}</span>
      </div>
      <span className="text-xs text-muted-foreground">{description}</span>
    </Button>
  );

  return (
    <AdminProtection>
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        
        <main className="container mx-auto px-4 pt-24 pb-8">
          <div className="max-w-7xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-gray-600">
                  Manage sequences, process claims, and maintain the marketplace
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-500">Administrator Panel</span>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TabButton
                tab="editor"
                icon={<Upload className="h-5 w-5" />}
                label="Sequence Editor"
                description="Manage sequences and update metadata"
              />
              <TabButton
                tab="claims"
                icon={<UserCheck className="h-5 w-5" />}
                label="Claims Management"
                description="Review and process ownership claims"
              />
            </div>

            {/* Content based on active tab */}
            {activeTab === 'editor' && (
              <>
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Total Sequences
                      </CardTitle>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats.totalSequences}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        With Spotify Data
                      </CardTitle>
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats.withSpotifyData}</div>
                      <p className="text-xs text-muted-foreground">
                        {stats.totalSequences > 0 
                          ? Math.round((stats.withSpotifyData / stats.totalSequences) * 100)
                          : 0}% coverage
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        With Artists
                      </CardTitle>
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats.withArtist}</div>
                      <p className="text-xs text-muted-foreground">
                        {stats.totalSequences > 0 
                          ? Math.round((stats.withArtist / stats.totalSequences) * 100)
                          : 0}% coverage
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        With Genres
                      </CardTitle>
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{stats.withGenre}</div>
                      <p className="text-xs text-muted-foreground">
                        {stats.totalSequences > 0 
                          ? Math.round((stats.withGenre / stats.totalSequences) * 100)
                          : 0}% coverage
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Missing Metadata
                      </CardTitle>
                      <RefreshCw className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-orange-600">
                        {stats.missingMetadata}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full"
                        onClick={() => setShowAutoUpdate(true)}
                        disabled={loading || stats.missingMetadata === 0}
                      >
                        Auto-Update
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Main Editor */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="h-5 w-5" />
                      Sequence Editor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SequenceEditor />
                  </CardContent>
                </Card>
              </>
            )}

            {activeTab === 'claims' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5" />
                    Ownership Claims Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ClaimsManagement />
                </CardContent>
              </Card>
            )}
          </div>
        </main>

        <Footer />

        {/* Auto-update modal */}
        {showAutoUpdate && (
          <AutoUpdateModal
            isOpen={showAutoUpdate}
            onClose={() => setShowAutoUpdate(false)}
          />
        )}
      </div>
    </AdminProtection>
  );
};

export default AdminPage;